import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Zap } from "lucide-react";
import api from "@/lib/api";

/**
 * Configuração do auto-sync do estado Zoho pela jornada do cliente
 * (25/02/2026). Admin pode:
 *   * Ver os defaults por evento
 *   * Sobrepor com um label custom (ex.: tenant usa "Won" em vez de "Convertido")
 *   * Desligar um evento individual (deixando o campo vazio)
 */
const EVENT_LABEL = {
  lead_created:       "Lead criado (primeiro registo)",
  first_contact:      "Primeiro contacto SAC feito",
  attempted_contact:  "Tentativa de contacto (do PBX Linkus)",
  estimate_sent:      "Estimativa enviada ao cliente",
  rt_scheduled:       "Reunião Técnica agendada",
  rt_completed:       "Reunião Técnica concluída",
  proposal_sent:      "Proposta enviada ao cliente",
  accepted:           "Proposta aceite (contrato assinado)",
  rejected:           "Cliente recusou / perdido",
};

export default function ZohoJourneySettings() {
  const [events, setEvents] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/zoho/status-mapping");
      setEvents(data.events || []);
      setDefaults(data.defaults || {});
      // Merge defaults with custom for editing UX
      const merged = {};
      for (const e of data.events || []) {
        merged[e] = data.custom?.[e] !== undefined ? data.custom[e] : (data.defaults?.[e] || "");
      }
      setDraft(merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar mapeamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/zoho/status-mapping", { mapping: draft });
      toast.success("Mapeamento gravado — as próximas transições passam já a usar estes valores.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar");
    } finally { setSaving(false); }
  };

  const resetToDefault = (event) => {
    setDraft(d => ({ ...d, [event]: defaults[event] || "" }));
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">A carregar…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="zoho-journey-settings">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Auto-sync do estado Zoho</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Actualização automática do <code className="px-1 py-0.5 rounded bg-muted text-foreground text-[13px]">Lead_Status</code> em Zoho CRM à medida que o cliente avança na jornada.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-[hsl(var(--warning))]/25 bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] text-sm leading-relaxed px-4 py-3 mb-5">
        <b>Nota:</b> Se o teu tenant Zoho tem os valores do picklist em Inglês ou traduzidos, sobrepõe aqui.
        Deixa em branco para <b>desactivar</b> o auto-sync desse evento específico (o estado no Zoho fica intacto).
      </div>

      <div className="rounded-lg border border-border bg-card divide-y divide-slate-100">
        {/* Cabeçalho */}
        <div className="hidden md:grid md:grid-cols-[1fr,minmax(280px,340px),40px] gap-4 px-4 py-2 bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div>Evento na jornada</div>
          <div>Estado a definir no Zoho</div>
          <div />
        </div>

        {events.map(ev => {
          const isCustom = draft[ev] !== (defaults[ev] || "");
          return (
            <div
              key={ev}
              className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr,minmax(280px,340px),40px] gap-3 md:gap-4 md:items-center"
              data-testid={`journey-row-${ev}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground break-words">
                  {EVENT_LABEL[ev] || ev}
                </div>
                <div className="mt-1 text-xs text-muted-foreground font-mono break-all">
                  <span className="text-muted-foreground">{ev}</span>
                  <span className="mx-1.5 text-muted-foreground/70">·</span>
                  default:{" "}
                  <b className="text-foreground font-semibold" title={defaults[ev]}>
                    {defaults[ev] || "—"}
                  </b>
                </div>
              </div>

              <div className="min-w-0">
                <input
                  type="text"
                  value={draft[ev] ?? ""}
                  onChange={e => setDraft(d => ({ ...d, [ev]: e.target.value }))}
                  placeholder="(desactivado — deixa em branco)"
                  title={draft[ev] || ""}
                  data-testid={`journey-input-${ev}`}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
                />
              </div>

              <div className="flex md:justify-end">
                {isCustom ? (
                  <button
                    type="button"
                    onClick={() => resetToDefault(ev)}
                    title="Repor default"
                    data-testid={`journey-reset-${ev}`}
                    className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                ) : (
                  <span className="hidden md:block w-4 h-4" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="journey-save-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar mapeamento"}
        </button>
      </div>
    </div>
  );
}
